/**
 * StatusBadge.
 *
 * Per UX principle 3: "One canonical status indicator per entity, identical
 * everywhere." This component owns the mapping from domain status enums to
 * their visual tone + label + icon. Use it for project lifecycle, parts
 * workflow, BG status, batch status, inspection outcome.
 *
 * Color is informational only — the badge always includes the word, so a
 * monochrome viewer still gets the meaning (WCAG: color never sole carrier).
 */
import type {
  BankGuaranteeStatus,
  InspectionOutcome,
  OrderBatchStatus,
  PartsWorkflowState,
  ProjectLifecycleState,
} from '@procurement/shared';
import {
  Ban,
  CheckCircle2,
  CircleDot,
  CircleX,
  Clock,
  FilePenLine,
  Lock,
  RefreshCw,
} from 'lucide-react';
import { Badge, type BadgeTone } from './badge';
import { cn } from '@/lib/cn';
import type { ReactNode } from 'react';

interface StatusDef {
  label: string;
  tone: BadgeTone;
  icon: ReactNode;
}

// Project lifecycle
// Patch P1 — each of the seven states gets a distinct tone instead of the
// prior collapse onto three (info/warning/success). Progression now reads
// as a visual escalation: blue → blue → indigo (Contract signed) → amber
// (pending) → green (active) → orange (awaiting payment) → muted (closed).
const lifecycleDefs: Record<ProjectLifecycleState, StatusDef> = {
  DFA_RECEIVED: { label: 'DFA Received', tone: 'info', icon: <FilePenLine className="h-3 w-3" /> },
  DFA_SUBMITTED: {
    label: 'DFA Submitted',
    tone: 'info',
    icon: <FilePenLine className="h-3 w-3" />,
  },
  CONTRACT_SIGNED: { label: 'Contract Signed', tone: 'accent', icon: <CheckCircle2 className="h-3 w-3" /> },
  PENDING_PARTS_APPROVAL: {
    label: 'Pending Approval',
    tone: 'warning',
    icon: <Clock className="h-3 w-3" />,
  },
  ACTIVE: { label: 'Active', tone: 'success', icon: <CircleDot className="h-3 w-3" /> },
  AWAITING_PAYMENT: {
    label: 'Awaiting Payment',
    tone: 'attention',
    icon: <Clock className="h-3 w-3" />,
  },
  CLOSED: { label: 'Closed', tone: 'neutral', icon: <CheckCircle2 className="h-3 w-3" /> },
};

// Parts workflow (stored state)
const partsWorkflowDefs: Record<PartsWorkflowState, StatusDef> = {
  NOT_STARTED: { label: 'Not Started', tone: 'neutral', icon: <CircleDot className="h-3 w-3" /> },
  ENTERING: { label: 'Entering', tone: 'info', icon: <FilePenLine className="h-3 w-3" /> },
  PENDING_APPROVAL: {
    label: 'Pending Approval',
    tone: 'warning',
    icon: <Clock className="h-3 w-3" />,
  },
  FINALISED: { label: 'Finalised', tone: 'success', icon: <Lock className="h-3 w-3" /> },
};

// Order batch
// Patch P1 — ACTIVE moves from info → success (a healthy in-flight batch
// is the good state). CANCELLED and REPLACED unchanged.
const batchDefs: Record<OrderBatchStatus, StatusDef> = {
  ACTIVE: { label: 'Active', tone: 'success', icon: <CircleDot className="h-3 w-3" /> },
  CANCELLED: { label: 'Cancelled', tone: 'danger', icon: <Ban className="h-3 w-3" /> },
  REPLACED: { label: 'Replaced', tone: 'neutral', icon: <RefreshCw className="h-3 w-3" /> },
};

// Bank Guarantee
// Patch P1 — RELEASED moves from success → neutral (released = retired, not
// a celebrated state). ACCEPTED stays success since it's the in-force gate.
const bgDefs: Record<BankGuaranteeStatus, StatusDef> = {
  ACTIVE: { label: 'Active', tone: 'info', icon: <CircleDot className="h-3 w-3" /> },
  ACCEPTED: { label: 'Accepted', tone: 'success', icon: <CheckCircle2 className="h-3 w-3" /> },
  ENCASHED: { label: 'Encashed', tone: 'danger', icon: <CircleX className="h-3 w-3" /> },
  RELEASED: { label: 'Released', tone: 'neutral', icon: <CheckCircle2 className="h-3 w-3" /> },
  REPLACED: { label: 'Replaced', tone: 'neutral', icon: <RefreshCw className="h-3 w-3" /> },
};

// Inspection outcome
const inspectionDefs: Record<InspectionOutcome, StatusDef> = {
  ACCEPTED: { label: 'Accepted', tone: 'success', icon: <CheckCircle2 className="h-3 w-3" /> },
  OBSERVATION: { label: 'Observation', tone: 'warning', icon: <Clock className="h-3 w-3" /> },
  REJECTED: { label: 'Rejected', tone: 'danger', icon: <CircleX className="h-3 w-3" /> },
};

export type StatusBadgeProps =
  | { kind: 'lifecycle'; value: ProjectLifecycleState; className?: string }
  | { kind: 'partsWorkflow'; value: PartsWorkflowState; className?: string }
  | { kind: 'batch'; value: OrderBatchStatus; className?: string }
  | { kind: 'bg'; value: BankGuaranteeStatus; className?: string }
  | { kind: 'inspection'; value: InspectionOutcome; className?: string };

export function StatusBadge(props: StatusBadgeProps) {
  let def: StatusDef;
  switch (props.kind) {
    case 'lifecycle':
      def = lifecycleDefs[props.value];
      break;
    case 'partsWorkflow':
      def = partsWorkflowDefs[props.value];
      break;
    case 'batch':
      def = batchDefs[props.value];
      break;
    case 'bg':
      def = bgDefs[props.value];
      break;
    case 'inspection':
      def = inspectionDefs[props.value];
      break;
  }
  return (
    <Badge tone={def.tone} className={cn(props.className)}>
      {def.icon}
      {def.label}
    </Badge>
  );
}
